import {
  AddClientToOrderModalCreatePayload,
  AddClientToOrderModalSubmitPayload,
  AddOrderDiscountModalSubmitPayload,
  AddOrderItemModalSubmitPayload,
  AddWaiterToOrderModalSubmitPayload,
  SettleOrderCreditSubmitPayload,
  SettleOrderDirectSubmitPayload,
  SettleOrderOtherSubmitPayload,
  SettleOrderSplitSubmitPayload,
  ShiftOrderSubmitPayload,
} from '@/renderer/types/sales';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import {
  assignClientForSelectedOrder,
  closeAddItemModal,
  closeClientModal,
  closeCreateOrderModal,
  closeDiscountModal,
  closeKotNumberModal,
  closeOpenNewDayModal,
  closeOrderShiftModal,
  closePrintReceiptPrompt,
  closeQuickActionsModal,
  closeSalesReportModal,
  closeSettleModal,
  closeWaiterModal,
  confirmCreateOrderForSelectedTable,
  confirmKotForSelectedOrder,
  confirmPrintReceiptForSettlement,
  createClientForSelectedOrder,
  exportSalesReportPdfForCurrentOutlet,
  getTodayIsoDate,
  openNewDayForCurrentOutlet,
  openOpenNewDayModal,
  openSalesReportForCurrentOutlet,
  removeSelectedOrderDiscount,
  selectCanCreateOrders,
  selectCanOpenNewDay,
  selectCompanyClients,
  selectCreateOrderTargetTable,
  selectCurrentBusinessDayLabel,
  selectCurrentOutletName,
  selectDiningSectionsWithTables,
  selectIsAddItemModalOpen,
  selectIsAddingOrderItem,
  selectIsApplyingDiscount,
  selectIsAssigningClientToOrder,
  selectIsClientModalOpen,
  selectIsConfirmingKot,
  selectIsCreateOrderModalOpen,
  selectIsCreatingClient,
  selectIsCreatingOrder,
  selectIsDiscountModalOpen,
  selectIsExportingSalesReport,
  selectIsKotNumberModalOpen,
  selectIsLoadingClientModalData,
  selectIsLoadingSalesReport,
  selectIsLoadingSettleOptions,
  selectIsLoadingWaiters,
  selectIsOpenNewDayModalOpen,
  selectIsOpeningNewDay,
  selectIsOrderShiftModalOpen,
  selectIsPreparingOrderShift,
  selectIsPrintReceiptPromptOpen,
  selectIsPrintingReceipt,
  selectIsQuickActionsModalOpen,
  selectIsRemovingDiscount,
  selectIsSalesReportModalOpen,
  selectIsSendingReportEmail,
  selectIsSettleModalOpen,
  selectIsShiftingOrder,
  selectIsSubmittingSettlement,
  selectIsUpdatingOrderWaiter,
  selectIsWaiterModalOpen,
  selectMenuItemForModal,
  selectNextBusinessDayValue,
  selectOpenNewDayTargetDate,
  selectOutletWaiters,
  selectPendingReceiptContext,
  selectSalesReport,
  selectSelectedOrder,
  selectSettlementClients,
  selectSettlementPaymentMethods,
  sendSalesReportEmailForCurrentOutlet,
  setOpenNewDayTargetDate,
  shiftIsoDateByDays,
  submitCreditSettlementForSelectedOrder,
  submitDirectSettlementForSelectedOrder,
  submitOrderShiftForSelectedOrder,
  submitOtherSettlementForSelectedOrder,
  submitSelectedOrderDiscount,
  submitSelectedOrderItem,
  submitSelectedOrderWaiter,
  submitSplitSettlementForSelectedOrder,
} from '@store/runningOrder';
import React, { useCallback } from 'react';
import { AddClientToOrderModal } from './AddClientToOrderModal';
import { AddDiscountToOrderModal } from './AddDiscountToOrderModal';
import { AddItemToOrderModal } from './AddItemToOrderModal';
import { AddWaiterToOrderModal } from './AddWaiterToOrderModal';
import { CreateOrderConfirmationModal } from './CreateOrderConfirmationModal';
import { KotPhysicalNumberModal } from './KotPhysicalNumberModal';
import { OpenNewDayModal } from './OpenNewDayModal';
import { OrderShiftModal } from './OrderShiftModal';
import { PrintReceiptConfirmationModal } from './PrintReceiptConfirmationModal';
import { QuickActionsModal } from './QuickActionsModal';
import { SalesReportModal } from './SalesReportModal';
import { SettleOrderModal } from './SettleOrderModal';

export const RunningOrderModals: React.FC = () => {
  const dispatch = useAppDispatch();

  const selectedOrder = useAppSelector(selectSelectedOrder);
  const createOrderTargetTable = useAppSelector(selectCreateOrderTargetTable);
  const canCreateOrders = useAppSelector(selectCanCreateOrders);
  const canOpenNewDay = useAppSelector(selectCanOpenNewDay);
  const outletName = useAppSelector(selectCurrentOutletName);
  const businessDayLabel = useAppSelector(selectCurrentBusinessDayLabel);
  const nextBusinessDayValue = useAppSelector(selectNextBusinessDayValue);
  // Recomputed on every render (cheap) rather than memoized once, so a POS
  // session left open across midnight doesn't get stuck with a max date
  // frozen on the day it was first mounted. Local calendar day, not UTC, so
  // outlets ahead of UTC don't see "today" fall outside the allowed range.
  const todayIsoDate = getTodayIsoDate();
  const maxBusinessDayValue = shiftIsoDateByDays(todayIsoDate, 1);
  // Today must always be selectable on the calendar even if the current
  // business day (day_open) is already today or later, so a user can
  // correct a misclick back to today instead of it being disabled — the
  // submit guard in openNewDayForCurrentOutlet still rejects a target that
  // isn't actually after the current business day.
  const minBusinessDayValue =
    todayIsoDate < nextBusinessDayValue ? todayIsoDate : nextBusinessDayValue;

  const isCreateOrderModalOpen = useAppSelector(selectIsCreateOrderModalOpen);
  const isCreatingOrder = useAppSelector(selectIsCreatingOrder);

  const isAddItemModalOpen = useAppSelector(selectIsAddItemModalOpen);
  const menuItemForModal = useAppSelector(selectMenuItemForModal);
  const isAddingOrderItem = useAppSelector(selectIsAddingOrderItem);

  const isDiscountModalOpen = useAppSelector(selectIsDiscountModalOpen);
  const isApplyingDiscount = useAppSelector(selectIsApplyingDiscount);
  const isRemovingDiscount = useAppSelector(selectIsRemovingDiscount);

  const isKotNumberModalOpen = useAppSelector(selectIsKotNumberModalOpen);
  const isConfirmingKot = useAppSelector(selectIsConfirmingKot);

  const isSettleModalOpen = useAppSelector(selectIsSettleModalOpen);
  const isLoadingSettleOptions = useAppSelector(selectIsLoadingSettleOptions);
  const isSubmittingSettlement = useAppSelector(selectIsSubmittingSettlement);
  const settlementPaymentMethods = useAppSelector(
    selectSettlementPaymentMethods,
  );
  const settlementClients = useAppSelector(selectSettlementClients);

  const isPrintReceiptPromptOpen = useAppSelector(
    selectIsPrintReceiptPromptOpen,
  );
  const pendingReceiptContext = useAppSelector(selectPendingReceiptContext);
  const isPrintingReceipt = useAppSelector(selectIsPrintingReceipt);

  const isClientModalOpen = useAppSelector(selectIsClientModalOpen);
  const isLoadingClientModalData = useAppSelector(
    selectIsLoadingClientModalData,
  );
  const isAssigningClientToOrder = useAppSelector(
    selectIsAssigningClientToOrder,
  );
  const isCreatingClient = useAppSelector(selectIsCreatingClient);
  const companyClients = useAppSelector(selectCompanyClients);

  const isWaiterModalOpen = useAppSelector(selectIsWaiterModalOpen);
  const isLoadingWaiters = useAppSelector(selectIsLoadingWaiters);
  const isUpdatingOrderWaiter = useAppSelector(selectIsUpdatingOrderWaiter);
  const outletWaiters = useAppSelector(selectOutletWaiters);

  const diningSections = useAppSelector(selectDiningSectionsWithTables);
  const isOrderShiftModalOpen = useAppSelector(selectIsOrderShiftModalOpen);
  const isPreparingOrderShift = useAppSelector(selectIsPreparingOrderShift);
  const isShiftingOrder = useAppSelector(selectIsShiftingOrder);

  const isQuickActionsModalOpen = useAppSelector(selectIsQuickActionsModalOpen);
  const isOpenNewDayModalOpen = useAppSelector(selectIsOpenNewDayModalOpen);
  const isSalesReportModalOpen = useAppSelector(selectIsSalesReportModalOpen);
  const isLoadingSalesReport = useAppSelector(selectIsLoadingSalesReport);
  const isOpeningNewDay = useAppSelector(selectIsOpeningNewDay);
  const isExportingSalesReport = useAppSelector(selectIsExportingSalesReport);
  const isSendingReportEmail = useAppSelector(selectIsSendingReportEmail);
  const openNewDayTargetDate = useAppSelector(selectOpenNewDayTargetDate);
  const salesReport = useAppSelector(selectSalesReport);

  const handleCancelCreateOrder = useCallback(() => {
    if (isCreatingOrder) return;
    dispatch(closeCreateOrderModal());
  }, [dispatch, isCreatingOrder]);

  const handleConfirmCreateOrder = useCallback(async () => {
    await dispatch(confirmCreateOrderForSelectedTable());
  }, [dispatch]);

  const handleCloseAddItemModal = useCallback(() => {
    if (isAddingOrderItem) return;
    dispatch(closeAddItemModal());
  }, [dispatch, isAddingOrderItem]);

  const handleSubmitAddOrderItem = useCallback(
    async ({ notes, quantity }: AddOrderItemModalSubmitPayload) => {
      await dispatch(submitSelectedOrderItem({ notes, quantity }));
    },
    [dispatch],
  );

  const handleCloseDiscountModal = useCallback(() => {
    if (isApplyingDiscount) return;
    dispatch(closeDiscountModal());
  }, [dispatch, isApplyingDiscount]);

  const handleSubmitDiscount = useCallback(
    async ({ notes, type, value }: AddOrderDiscountModalSubmitPayload) => {
      await dispatch(submitSelectedOrderDiscount({ notes, type, value }));
    },
    [dispatch],
  );

  const handleRemoveDiscount = useCallback(async () => {
    await dispatch(removeSelectedOrderDiscount());
  }, [dispatch]);

  const handleCloseKotNumberModal = useCallback(() => {
    if (isConfirmingKot) return;
    dispatch(closeKotNumberModal());
  }, [dispatch, isConfirmingKot]);

  const handleSubmitKotNumber = useCallback(
    async (physicalKotNumber: string) => {
      await dispatch(confirmKotForSelectedOrder(physicalKotNumber));
    },
    [dispatch],
  );

  const handleCloseSettleModal = useCallback(() => {
    if (isSubmittingSettlement) return;
    dispatch(closeSettleModal());
  }, [dispatch, isSubmittingSettlement]);

  const handleCloseClientModal = useCallback(() => {
    if (isAssigningClientToOrder || isCreatingClient) return;
    dispatch(closeClientModal());
  }, [dispatch, isAssigningClientToOrder, isCreatingClient]);

  const handleCloseWaiterModal = useCallback(() => {
    if (isUpdatingOrderWaiter) return;
    dispatch(closeWaiterModal());
  }, [dispatch, isUpdatingOrderWaiter]);

  const handleCloseOrderShiftModal = useCallback(() => {
    if (isShiftingOrder) return;
    dispatch(closeOrderShiftModal());
  }, [dispatch, isShiftingOrder]);

  const handleCloseQuickActionsModal = useCallback(() => {
    if (isLoadingSalesReport || isOpeningNewDay) return;
    dispatch(closeQuickActionsModal());
  }, [dispatch, isLoadingSalesReport, isOpeningNewDay]);

  const handleCloseOpenNewDayModal = useCallback(() => {
    if (isOpeningNewDay) return;
    dispatch(closeOpenNewDayModal());
  }, [dispatch, isOpeningNewDay]);

  const handleExportSalesReportPdf = useCallback(() => {
    if (isExportingSalesReport) return;
    dispatch(exportSalesReportPdfForCurrentOutlet());
  }, [dispatch, isExportingSalesReport]);

  const handleSendSalesReportEmail = useCallback(() => {
    if (isSendingReportEmail) return;
    dispatch(sendSalesReportEmailForCurrentOutlet());
  }, [dispatch, isSendingReportEmail]);

  const handleCloseSalesReportModal = useCallback(() => {
    if (isExportingSalesReport) return;
    dispatch(closeSalesReportModal());
  }, [dispatch, isExportingSalesReport]);

  const handleChangeOpenNewDayTargetDate = useCallback(
    (value: string) => {
      dispatch(setOpenNewDayTargetDate(value));
    },
    [dispatch],
  );

  const handleSubmitDirectSettlement = useCallback(
    async ({ notes, paymentMethodId }: SettleOrderDirectSubmitPayload) => {
      await dispatch(
        submitDirectSettlementForSelectedOrder({
          notes,
          paymentMethodId,
        }),
      );
    },
    [dispatch],
  );

  const handleSubmitSplitSettlement = useCallback(
    async ({ notes, payments }: SettleOrderSplitSubmitPayload) => {
      await dispatch(
        submitSplitSettlementForSelectedOrder({
          notes,
          payments,
        }),
      );
    },
    [dispatch],
  );

  const handleSubmitCreditSettlement = useCallback(
    async ({ clientId, notes }: SettleOrderCreditSubmitPayload) => {
      await dispatch(
        submitCreditSettlementForSelectedOrder({
          clientId,
          notes,
        }),
      );
    },
    [dispatch],
  );

  const handleSubmitOtherSettlement = useCallback(
    async ({ notes, type }: SettleOrderOtherSubmitPayload) => {
      await dispatch(
        submitOtherSettlementForSelectedOrder({
          notes,
          type,
        }),
      );
    },
    [dispatch],
  );

  const handleConfirmPrintReceipt = useCallback(() => {
    dispatch(confirmPrintReceiptForSettlement());
  }, [dispatch]);

  const handleDeclinePrintReceipt = useCallback(() => {
    dispatch(closePrintReceiptPrompt());
  }, [dispatch]);

  const handleAssignClientToOrder = useCallback(
    async ({ clientId }: AddClientToOrderModalSubmitPayload) => {
      await dispatch(assignClientForSelectedOrder({ clientId }));
    },
    [dispatch],
  );

  const handleCreateClientForOrder = useCallback(
    async ({
      address,
      contact,
      email,
      firstName,
      lastName,
      tinNumber,
    }: AddClientToOrderModalCreatePayload) => {
      await dispatch(
        createClientForSelectedOrder({
          firstName,
          lastName,
          address: address || '',
          contact: contact || '',
          email: email || '',
          tinNumber: tinNumber || '',
        }),
      );
    },
    [dispatch],
  );

  const handleSubmitWaiter = useCallback(
    async ({ waiterId }: AddWaiterToOrderModalSubmitPayload) => {
      await dispatch(submitSelectedOrderWaiter({ waiterId }));
    },
    [dispatch],
  );

  const handleSubmitOrderShift = useCallback(
    async ({ targetTableId }: ShiftOrderSubmitPayload) => {
      await dispatch(submitOrderShiftForSelectedOrder({ targetTableId }));
    },
    [dispatch],
  );

  const handleOpenSalesReport = useCallback(async () => {
    await dispatch(openSalesReportForCurrentOutlet());
  }, [dispatch]);

  const handleOpenNewDay = useCallback(() => {
    dispatch(openOpenNewDayModal(nextBusinessDayValue));
  }, [dispatch, nextBusinessDayValue]);

  const handleSubmitOpenNewDay = useCallback(async () => {
    const targetDate = openNewDayTargetDate || nextBusinessDayValue;
    await dispatch(openNewDayForCurrentOutlet(targetDate));
  }, [dispatch, nextBusinessDayValue, openNewDayTargetDate]);

  return (
    <>
      <QuickActionsModal
        isOpen={isQuickActionsModalOpen}
        outletName={outletName}
        businessDayLabel={businessDayLabel}
        isLoadingSalesReport={isLoadingSalesReport}
        isOpeningNewDay={isOpeningNewDay}
        canOpenNewDay={canOpenNewDay}
        onClose={handleCloseQuickActionsModal}
        onViewSalesReport={handleOpenSalesReport}
        onOpenNewDay={handleOpenNewDay}
      />

      <OpenNewDayModal
        isOpen={isOpenNewDayModalOpen}
        currentBusinessDayLabel={businessDayLabel}
        selectedDate={openNewDayTargetDate || nextBusinessDayValue}
        minDate={minBusinessDayValue}
        maxDate={maxBusinessDayValue}
        isSubmitting={isOpeningNewDay}
        onClose={handleCloseOpenNewDayModal}
        onDateChange={handleChangeOpenNewDayTargetDate}
        onSubmit={handleSubmitOpenNewDay}
      />

      <SalesReportModal
        isOpen={isSalesReportModalOpen}
        report={salesReport}
        outletName={outletName}
        businessDayLabel={businessDayLabel}
        isLoading={isLoadingSalesReport}
        isExporting={isExportingSalesReport}
        isSendingEmail={isSendingReportEmail}
        onClose={handleCloseSalesReportModal}
        onExport={handleExportSalesReportPdf}
        onSendEmail={handleSendSalesReportEmail}
      />

      <CreateOrderConfirmationModal
        isOpen={isCreateOrderModalOpen}
        tableName={createOrderTargetTable?.name || ''}
        onCancel={handleCancelCreateOrder}
        onConfirm={handleConfirmCreateOrder}
        isSubmitting={isCreatingOrder}
        isConfirmDisabled={!canCreateOrders}
        confirmHelpText={
          canCreateOrders
            ? null
            : 'Your account does not have permission to create new orders.'
        }
      />

      <AddItemToOrderModal
        isOpen={isAddItemModalOpen}
        item={menuItemForModal}
        onClose={handleCloseAddItemModal}
        onSubmit={handleSubmitAddOrderItem}
        isSubmitting={isAddingOrderItem}
      />

      <AddDiscountToOrderModal
        isOpen={isDiscountModalOpen}
        orderTotal={selectedOrder?.total_amount || 0}
        existingDiscount={selectedOrder?.discount_amount || 0}
        onClose={handleCloseDiscountModal}
        onSubmit={handleSubmitDiscount}
        onRemove={handleRemoveDiscount}
        isSubmitting={isApplyingDiscount}
        isRemoving={isRemovingDiscount}
      />

      <KotPhysicalNumberModal
        isOpen={isKotNumberModalOpen}
        onClose={handleCloseKotNumberModal}
        onSubmit={handleSubmitKotNumber}
        isSubmitting={isConfirmingKot}
      />

      <SettleOrderModal
        isOpen={isSettleModalOpen}
        order={selectedOrder}
        paymentMethods={settlementPaymentMethods}
        clients={settlementClients}
        isPaymentMethodsLoading={isLoadingSettleOptions}
        isSubmitting={isSubmittingSettlement}
        onClose={handleCloseSettleModal}
        onSubmitDirect={handleSubmitDirectSettlement}
        onSubmitSplit={handleSubmitSplitSettlement}
        onSubmitCredit={handleSubmitCreditSettlement}
        onSubmitOther={handleSubmitOtherSettlement}
      />

      <PrintReceiptConfirmationModal
        isOpen={isPrintReceiptPromptOpen}
        billNumber={pendingReceiptContext?.order.bill_number}
        isPrinting={isPrintingReceipt}
        onConfirm={handleConfirmPrintReceipt}
        onCancel={handleDeclinePrintReceipt}
      />

      <AddClientToOrderModal
        isOpen={isClientModalOpen}
        order={selectedOrder}
        clients={companyClients}
        isLoading={isLoadingClientModalData}
        isAssigning={isAssigningClientToOrder}
        isCreating={isCreatingClient}
        onClose={handleCloseClientModal}
        onAssignClient={handleAssignClientToOrder}
        onCreateClient={handleCreateClientForOrder}
      />

      <AddWaiterToOrderModal
        isOpen={isWaiterModalOpen}
        order={selectedOrder}
        waiters={outletWaiters}
        isLoading={isLoadingWaiters}
        isSubmitting={isUpdatingOrderWaiter}
        onClose={handleCloseWaiterModal}
        onSubmit={handleSubmitWaiter}
      />

      <OrderShiftModal
        isOpen={isOrderShiftModalOpen}
        order={selectedOrder}
        sections={diningSections}
        isSubmitting={isShiftingOrder || isPreparingOrderShift}
        onClose={handleCloseOrderShiftModal}
        onSubmit={handleSubmitOrderShift}
      />
    </>
  );
};
